import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ShieldCheck } from "lucide-react";
import { api, errMsg, getAffiliate } from "@/lib/api";
import { hasAccess, useAuth } from "@/lib/auth";
import { Logo } from "@/components/AppShell";
import { Busy } from "@/components/ui-bits";
import { toast } from "sonner";

const Shell = ({ children }: any) => (
  <main className="min-h-screen bg-cream">
    <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center"><Logo /></div>
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 lg:py-16">{children}</div>
  </main>
);

export function Subscribe() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [plan, setPlan] = useState<any>(null);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/billing/plan").then((r) => setPlan(r.data)); api.get("/billing/payment-link").then((r) => setLink(r.data.url)).catch(() => {}); }, []);
  useEffect(() => { if (hasAccess(user)) nav(user.onboarding_complete ? "/app" : "/onboarding", { replace: true }); }, [user, nav]);

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin, affiliate_ref: getAffiliate() });
      try { window.top.location.href = data.checkout_url; } catch { window.location.href = data.checkout_url; }
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const recordIntent = () => { api.post("/billing/checkout", { origin_url: window.location.origin, affiliate_ref: getAffiliate() }).catch(() => {}); };

  return (
    <Shell>
      <div className="grid lg:grid-cols-[1fr_.9fr] gap-12 items-start animate-rise">
        <div>
          <p className="eyebrow">Step 2 of 2</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight">Lock in your <span className="display-italic">founding</span> price.</h1>
          <p className="text-ink-muted mt-4 max-w-lg">Hi {user?.name?.split(" ")[0] || "there"}. Email Bestie is paid from day one, so everything you set up is yours right away. Cancel any time and your data stays put.</p>
          <ul className="mt-8 space-y-3 text-sm">
            {["A strategy and calendar built around your offers and launches", "Every email written in your voice, with Voice Match", "Never invents proof, stories or results", "Sequences and automations you can paste anywhere", "Founding price stays yours while you're active"].map((t) => <li key={t} className="flex gap-3"><Check className="size-4 text-olive mt-0.5 shrink-0" /> {t}</li>)}
          </ul>
        </div>
        <div className="card-soft p-8" data-testid="subscribe-card">
          <span className="chip bg-olive-light text-olive-deep">{plan?.founding ? "Founding member" : "Monthly"}</span>
          <div className="flex items-end gap-2 mt-4"><span className="font-serif text-6xl" data-testid="subscribe-price">${plan?.price ?? 17}</span><span className="text-ink-muted mb-3">/month</span></div>
          {plan?.founding && <p className="text-sm text-ink-muted mt-1">Future price for new members: ${plan.future_price}/mo. Not you.</p>}
          {link ? (
            <a href={link} target="_top" rel="noopener" onClick={recordIntent} className="btn-ink w-full !py-4 mt-8 shadow-offset" data-testid="subscribe-checkout-button">Continue to payment</a>
          ) : (
            <button onClick={checkout} disabled={busy || !plan} className="btn-ink w-full !py-4 mt-8 shadow-offset" data-testid="subscribe-checkout-button">{busy ? <Busy label="Opening secure checkout..." /> : "Continue to payment"}</button>
          )}
          <p className="text-xs text-ink-soft mt-4 inline-flex items-center gap-1"><ShieldCheck className="size-3.5" /> Secure checkout by Stripe. No free trial, billed monthly.</p>
        </div>
      </div>
    </Shell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState<"checking" | "paid" | "slow" | "failed">("checking");
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("failed"); return; }
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); const u = await refresh(); setTimeout(() => nav(u?.onboarding_complete ? "/app" : "/onboarding", { replace: true }), 1200); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {}
      if (tries >= 8) { setState("slow"); return; }
      setTimeout(tick, 2000);
    };
    tick();
  }, [params, refresh, nav]);
  return (
    <Shell>
      <div className="max-w-lg mx-auto text-center card-soft p-10 animate-rise" data-testid="payment-success-card">
        {state === "checking" && <><h1 className="font-serif text-3xl">Confirming your payment...</h1><p className="text-ink-muted mt-3"><Busy label="Talking to Stripe" /></p></>}
        {state === "paid" && <><div className="size-14 rounded-full bg-olive-light text-olive-deep grid place-items-center mx-auto"><Check className="size-7" /></div><h1 className="font-serif text-3xl mt-4">You're in. Welcome, founding member.</h1><p className="text-ink-muted mt-3">Taking you to set up your profile.</p></>}
        {state === "slow" && <><h1 className="font-serif text-3xl">Almost there</h1><p className="text-ink-muted mt-3">Stripe is still confirming. Refresh in a moment, or continue and we'll check again.</p><button className="btn-ink mt-6" onClick={() => window.location.reload()} data-testid="payment-retry-button">Check again</button></>}
        {state === "failed" && <><h1 className="font-serif text-3xl">Payment didn't go through</h1><p className="text-ink-muted mt-3">No charge was made. You can try again whenever you're ready.</p><Link to="/subscribe" className="btn-ink mt-6" data-testid="payment-failed-retry">Back to checkout</Link></>}
      </div>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div className="max-w-lg mx-auto text-center card-soft p-10" data-testid="payment-cancel-card">
        <h1 className="font-serif text-3xl">No worries, nothing was charged</h1>
        <p className="text-ink-muted mt-3">Your account is saved. Come back to checkout whenever you're ready.</p>
        <Link to="/subscribe" className="btn-ink mt-6" data-testid="payment-cancel-back">Return to checkout</Link>
      </div>
    </Shell>
  );
}
